import { ShiftSummaryCategory } from "../../../reviewAndPublish/model";

/**
 * Shared process-path row derivation for a shiftSummary category, used by BOTH
 * the on-screen Process Path table (ShiftDataTable) and the PDF export
 * (downloadPlanPdf) so their rows/ordering/totals can't silently drift.
 *
 * Rows are one per unique process-path label, with the numeric value per time
 * slot (0 when the label is absent in a slot), sorted by the first time-slot
 * value descending. `totals` is the per-slot column sum.
 */
export interface ProcessPathRow {
  label: string;
  values: number[];
}

export interface ProcessPathData {
  /** Formatted "HH:MM - HH:MM" column labels, one per time slot. */
  timeSlots: string[];
  /** Process-path rows, sorted by the first time-slot value descending. */
  rows: ProcessPathRow[];
  /** Column sum per time slot (across all rows). */
  totals: number[];
}

export const buildProcessPathData = (category: ShiftSummaryCategory): ProcessPathData => {
  const planOutput = category.planOutput;
  const timeSlots = planOutput.map((slot) => `${slot.startTime} - ${slot.endTime}`);

  // Unique process-path labels across all time slots.
  const labels: string[] = [];
  planOutput.forEach((slot) => {
    slot.data.forEach((item) => {
      if (!labels.includes(item.label)) {
        labels.push(item.label);
      }
    });
  });

  const rows: ProcessPathRow[] = labels
    .map((label) => ({
      label,
      // Value per slot; 0 when the label is absent in that slot.
      values: planOutput.map((slot) => {
        const entry = slot.data.find((item) => item.label === label);
        return entry ? entry.intValue : 0;
      }),
    }))
    // Sort by the first time-slot value, descending. planOutput is non-empty
    // here, so values[0] is always present.
    .sort((a, b) => b.values[0] - a.values[0]);

  const totals = timeSlots.map((_, i) => rows.reduce((sum, row) => sum + row.values[i], 0));

  return { timeSlots, rows, totals };
};
